// acneScarsRemovalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const doctoroncall: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Doctor On Call in Dubai | Elite Body Home Polyclinic",
            "description": "Premium doctor home visit services in Dubai with DHA-certified physicians. Available 24/7 for urgent care, routine checkups, and chronic disease management. Book your appointment today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/doctor-on-call-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Doctor On Call in Dubai | Elite Body Home Polyclinic",
                "description": "Premium doctor home visit services in Dubai with DHA-certified physicians. Available 24/7 for urgent care, routine checkups, and chronic disease management. Book your appointment today.",
                "url": "https://elitebodyhome.com/services/doctor-on-call-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Doctor-Visit.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Doctor-Visit.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Doctor Home Visit Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Doctor On Call in Dubai | Elite Body Home Polyclinic",
                "description": "Premium doctor home visit services in Dubai with DHA-certified physicians. Available 24/7 for urgent care, routine checkups, and chronic disease management. Book your appointment today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Doctor-Visit.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Doctor On Call Dubai"],
        "hero": {
            "title": "Doctor On Call Services",
            "subtitle": "",
            "description": "From medical consultation to urgent care and routine check-ups, our DHA certified doctors provide personalized treatment in the comfort of your home, hotel or workplace. Avoid lengthy waiting times - premium healthcare is just a call away."
        },
        "howItWorks": {
            "title": "Doctor Home Visit Process",
            "description": "Our doctor on call service in Dubai brings world-class healthcare to your doorstep with convenience and efficiency."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Book Appointment",
                    "desc": "Schedule via phone call or online booking - we'll arrange a time that suits you.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Doctor Arrival",
                    "desc": "Our DHA-licensed doctor arrives at your location fully equipped for diagnosis and treatment.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Comprehensive Consultation",
                    "desc": "Thorough examination and discussion of your health concerns in private.",
                    "icon": "fa-solid fa-stethoscope"
                },
                {
                    "title": "Personalized Treatment",
                    "desc": "Immediate care with prescriptions if needed, plus a customized care plan.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Follow-up Care",
                    "desc": "Optional follow-up visits or remote monitoring as needed for your recovery.",
                    "icon": "fa-solid fa-notes-medical"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Doctor On Call",
            "subheading": "Why home doctor visits make sense",
            "para": "Choosing our doctor home visit service offers numerous advantages for patients in Dubai:",
            "items": [
                {
                    "title": "Immediate Care",
                    "desc": "Quick response times (30-45 minutes) for urgent medical needs.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Personalized Attention",
                    "desc": "One-on-one consultations without clinic distractions or time pressure.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "Comfort & Convenience",
                    "desc": "Receive care in your preferred environment - home, hotel or office.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "Senior-Friendly",
                    "desc": "Ideal solution for elderly patients with mobility challenges.",
                    "icon": "fa-solid fa-wheelchair"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit",
            "subheading": "",
            "para": "Our doctor on call service is perfect for:",
            "items": [
                {
                    "title": "Busy Professionals",
                    "desc": "Those who can't take time off work for clinic visits.",
                    "icon": "fa-solid fa-briefcase"
                },
                {
                    "title": "Families with Children",
                    "desc": "Parents needing pediatric care without clinic exposure.",
                    "icon": "fa-solid fa-children"
                },
                {
                    "title": "Post-Surgical Patients",
                    "desc": "Those recovering who need monitoring without hospital trips.",
                    "icon": "fa-solid fa-bed-pulse"
                },
                {
                    "title": "Chronic Condition Patients",
                    "desc": "Individuals needing regular checkups for diabetes, hypertension etc.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to Prepare",
            "subheading": "",
            "para": "To make the most of your home doctor visit:",
            "items": [
                {
                    "title": "List Symptoms",
                    "desc": "Note down all symptoms, their duration and severity.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "Prepare Medical History",
                    "desc": "Have previous test results and current medications ready.",
                    "icon": "fa-solid fa-folder-open"
                },
                {
                    "title": "Clear Space",
                    "desc": "Prepare a well-lit, comfortable area for the examination.",
                    "icon": "fa-solid fa-lightbulb"
                },
                {
                    "title": "Insurance Info",
                    "desc": "Have your insurance details available if needed.",
                    "icon": "fa-solid fa-file-invoice"
                }
            ]
        },
        "results": {
            "heading": "After Your Visit",
            "subheading": "",
            "para": "Following your doctor home visit, you can expect:",
            "items": [
                {
                    "title": "Clear Diagnosis",
                    "desc": "Professional assessment of your condition with explanations.",
                    "icon": "fa-solid fa-diagnoses"
                },
                {
                    "title": "Treatment Plan",
                    "desc": "Customized care instructions tailored to your needs.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "Prescriptions",
                    "desc": "If needed, medications will be prescribed electronically.",
                    "icon": "fa-solid fa-prescription"
                },
                {
                    "title": "Follow-up Options",
                    "desc": "Recommendations for further tests or follow-up visits.",
                    "icon": "fa-solid fa-calendar-plus"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Recommendations",
            "subheading": "",
            "para": "To ensure optimal recovery after your doctor visit:",
            "items": [
                {
                    "title": "Follow Instructions",
                    "desc": "Adhere strictly to prescribed medications and care plan.",
                    "icon": "fa-solid fa-list-check"
                },
                {
                    "title": "Monitor Symptoms",
                    "desc": "Track any changes in your condition and report concerns.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink plenty of fluids unless otherwise instructed.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "Rest Adequately",
                    "desc": "Allow your body time to heal with proper rest.",
                    "icon": "fa-solid fa-moon"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How quickly can a doctor come to my home?",
                    "answer": "We typically arrive within 30-45 minutes in Dubai for urgent cases, with flexible scheduling for routine visits."
                },
                {
                    "question": "What medical conditions can be treated at home?",
                    "answer": "We handle non-emergency cases including fevers, infections, minor injuries, chronic disease management, and routine checkups."
                },
                {
                    "question": "Are your doctors qualified to prescribe medication?",
                    "answer": "Yes, our DHA-licensed doctors can prescribe medications which can be delivered to your home or collected from nearby pharmacies."
                },
                {
                    "question": "Do you provide pediatric home doctor services?",
                    "answer": "Absolutely, we have specialized pediatric doctors for children's healthcare needs at home."
                },
                {
                    "question": "Is the service available 24/7?",
                    "answer": "Yes, our doctor on call service operates 365 days a year, 24 hours a day for your convenience."
                },
                {
                    "question": "What areas of Dubai do you cover?",
                    "answer": "We serve all areas of Dubai including Dubai Marina, Jumeirah, Downtown, and surrounding communities."
                }
            ]
        },
        "treatmentAreas1": [

        ],
        "treatmentAreas2": [

        ]
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/ebh-services-uae-37-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 356,
            "title": "Basic Doctor Home Visit Package",
            "price": 0,
            "description": "Comprehensive medical consultation in the comfort of your home. Includes full examination, diagnosis, basic treatment and care plan. Conducted by DHA-licensed doctors with 24/7 availability."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "الكشف المنزلي في دبي | عيادات ايليت بودي هوم",
            "description": "خدمات طبيب منزلي بدبي مع أطباء معتمدين من هيئة الصحة. متاح على مدار الساعة للرعاية العاجلة، الفحوصات الروتينية وإدارة الأمراض المزمنة. احجز موعدك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/doctor-on-call-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "الكشف المنزلي في دبي | عيادات ايليت بودي هوم",
                "description": "خدمات طبيب منزلي بدبي مع أطباء معتمدين من هيئة الصحة. متاح على مدار الساعة للرعاية العاجلة، الفحوصات الروتينية وإدارة الأمراض المزمنة. احجز موعدك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/doctor-on-call-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Doctor-Visit.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Doctor-Visit.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "الكشف المنزلي دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "الكشف المنزلي في دبي | عيادات ايليت بودي هوم",
                "description": "خدمات طبيب منزلي بدبي مع أطباء معتمدين من هيئة الصحة. متاح على مدار الساعة للرعاية العاجلة، الفحوصات الروتينية وإدارة الأمراض المزمنة. احجز موعدك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Doctor-Visit.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "الكشف المنزلي دبي"],
        "hero": {
            "title": "خدمات الطبيب عند الطلب",
            "subtitle": "",
            "description": "نوفر لك في ايليت بودي هوم خدمات الطبيب عند الطلب إلى باب منزلك عالمية المستوى مباشرةً. سواء كنت بحاجة إلى استشارة طبية، أو رعاية عاجلة، أو فحص روتيني، فإن فريقنا من الأطباء المعتمدين من هيئة الصحة بدبي (DHA) جاهز لتقديم رعاية شخصية وعلاج في راحة منزلك، أو فندقك، أو مكان عملك."
        },
        "howItWorks": {
            "title": "خطوات الكشف المنزلي",
            "description": "تعيد خدمة الطبيب عند الطلب لدينا تعريف مفهوم الراحة من خلال تقديم رعاية طبية متخصصة حتى باب منزلك."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "حجز الموعد",
                    "desc": "حدد موعدًا عبر الهاتف أو الحجز عبر الإنترنت - سنرتب وقتًا يناسبك.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "وصول الطبيب",
                    "desc": "يصل طبيبنا المعتمد من هيئة الصحة بدبي إلى موقعك مجهزًا بالكامل للتشخيص والعلاج.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "استشارة شاملة",
                    "desc": "فحص شامل ومناقشة لمشاكلك الصحية في خصوصية تامة.",
                    "icon": "fa-solid fa-stethoscope"
                },
                {
                    "title": "علاج مخصص",
                    "desc": "رعاية فورية مع وصفات طبية إذا لزم الأمر، بالإضافة إلى خطة رعاية مخصصة.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "رعاية المتابعة",
                    "desc": "زيارات متابعة اختيارية أو مراقبة عن بعد حسب الحاجة لتعافيك.",
                    "icon": "fa-solid fa-notes-medical"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد الكشف المنزلي",
            "subheading": "لماذا تعد زيارة الطبيب المنزلية خيارًا مثاليًا",
            "para": "يوفر اختيار خدمة الطبيب المنزلي العديد من المزايا للمرضى في دبي:",
            "items": [
                {
                    "title": "رعاية فورية",
                    "desc": "أوقات استجابة سريعة (30-45 دقيقة) للحالات الطبية العاجلة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "اهتمام شخصي",
                    "desc": "استشارات فردية دون إلهاءات العيادة أو ضغوط الوقت.",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "الراحة والسهولة",
                    "desc": "تلقي الرعاية في البيئة التي تفضلها - المنزل، الفندق أو المكتب.",
                    "icon": "fa-solid fa-couch"
                },
                {
                    "title": "ملائمة لكبار السن",
                    "desc": "حل مثالي للمرضى المسنين الذين يعانون من صعوبات في الحركة.",
                    "icon": "fa-solid fa-wheelchair"
                }
            ]
        },
        "eligibility": {
            "heading": "الفئات المستفيدة",
            "subheading": "",
            "para": "خدمة الطبيب عند الطلب لدينا مثالية ل:",
            "items": [
                {
                    "title": "المهنيون المشغولون",
                    "desc": "الذين لا يستطيعون أخذ إجازة من العمل لزيارات العيادة.",
                    "icon": "fa-solid fa-briefcase"
                },
                {
                    "title": "العائلات مع الأطفال",
                    "desc": "الآباء الذين يحتاجون إلى رعاية أطفال دون التعرض للعيادات.",
                    "icon": "fa-solid fa-children"
                },
                {
                    "title": "مرضى ما بعد الجراحة",
                    "desc": "أولئك الذين يتعافون والذين يحتاجون إلى المراقبة دون الحاجة إلى الذهاب إلى المستشفى.",
                    "icon": "fa-solid fa-bed-pulse"
                },
                {
                    "title": "مرضى الحالات المزمنة",
                    "desc": "الأفراد الذين يحتاجون إلى فحوصات منتظمة لمرض السكري، ارتفاع ضغط الدم وغيرها.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "preparationTips": {
            "heading": "كيفية الاستعداد",
            "subheading": "",
            "para": "للاستفادة القصوى من زيارة الطبيب المنزلية:",
            "items": [
                {
                    "title": "سجل الأعراض",
                    "desc": "دوّن جميع الأعراض، مدتها وشدتها.",
                    "icon": "fa-solid fa-clipboard-list"
                },
                {
                    "title": "جهز السجل الطبي",
                    "desc": "احتفظ بنتائج الاختبارات السابقة والأدوية الحالية جاهزة.",
                    "icon": "fa-solid fa-folder-open"
                },
                {
                    "title": "جهز مساحة مريحة",
                    "desc": "جهز منطقة مضاءة جيدًا ومريحة للفحص.",
                    "icon": "fa-solid fa-lightbulb"
                },
                {
                    "title": "معلومات التأمين",
                    "desc": "احتفظ بتفاصيل التأمين الخاصة بك في حالة الحاجة إليها.",
                    "icon": "fa-solid fa-file-invoice"
                }
            ]
        },
        "results": {
            "heading": "بعد الزيارة",
            "subheading": "",
            "para": "بعد زيارة الطبيب المنزلية، يمكنك توقع:",
            "items": [
                {
                    "title": "تشخيص واضح",
                    "desc": "تقييم احترافي لحالتك مع تفسيرات.",
                    "icon": "fa-solid fa-diagnoses"
                },
                {
                    "title": "خطة علاج",
                    "desc": "تعليمات رعاية مخصصة مصممة خصيصًا لاحتياجاتك.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "وصفات طبية",
                    "desc": "إذا لزم الأمر، سيتم وصف الأدوية إلكترونيًا.",
                    "icon": "fa-solid fa-prescription"
                },
                {
                    "title": "خيارات المتابعة",
                    "desc": "توصيات لمزيد من الاختبارات أو زيارات المتابعة.",
                    "icon": "fa-solid fa-calendar-plus"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح ما بعد الرعاية",
            "subheading": "",
            "para": "لضمان التعافي الأمثل بعد زيارة الطبيب:",
            "items": [
                {
                    "title": "اتبع التعليمات",
                    "desc": "الالتزام الصارم بالأدوية الموصوفة وخطة الرعاية.",
                    "icon": "fa-solid fa-list-check"
                },
                {
                    "title": "راقب الأعراض",
                    "desc": "تتبع أي تغييرات في حالتك وبلغ عن المخاوف.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "حافظ على رطوبة الجسم",
                    "desc": "اشرب الكثير من السوائل ما لم يُنصح بخلاف ذلك.",
                    "icon": "fa-solid fa-bottle-water"
                },
                {
                    "title": "احصل على راحة كافية",
                    "desc": "امنح جسمك الوقت الكافي للشفاء مع الراحة المناسبة.",
                    "icon": "fa-solid fa-moon"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "ما مدى سرعة وصول الطبيب إلى منزلي؟",
                    "answer": "نصل عادةً خلال 30-45 دقيقة في دبي للحالات العاجلة، مع جدولة مرنة للزيارات الروتينية."
                },
                {
                    "question": "ما هي الحالات الطبية التي يمكن علاجها في المنزل؟",
                    "answer": "نحن نتعامل مع الحالات غير الطارئة بما في ذلك الحمى، الالتهابات، الإصابات الطفيفة، إدارة الأمراض المزمنة، والفحوصات الروتينية."
                },
                {
                    "question": "هل أطباؤكم مؤهلون لوصف الأدوية؟",
                    "answer": "نعم، يمكن لأطبائنا المعتمدين من هيئة الصحة بدبي وصف الأدوية التي يمكن تسليمها إلى منزلك أو جمعها من الصيدليات القريبة."
                },
                {
                    "question": "هل تقدمون خدمات طبيب أطفال منزلي؟",
                    "answer": "بالتأكيد، لدينا أطباء أطفال متخصصين لاحتياجات الرعاية الصحية للأطفال في المنزل."
                },
                {
                    "question": "هل الخدمة متاحة على مدار الساعة؟",
                    "answer": "نعم، تعمل خدمة الطبيب عند الطلب لدينا 365 يومًا في السنة، على مدار 24 ساعة لراحتك."
                },
                {
                    "question": "ما هي مناطق دبي التي تخدمونها؟",
                    "answer": "نحن نخدم جميع مناطق دبي بما في ذلك دبي مارينا، جميرا، داون تاون، والمجتمعات المحيطة."
                }
            ]
        },
        "treatmentAreas1": [

        ],
        "treatmentAreas2": [

        ]
    }
}